/* WHERE A CLIP IS, AT A GIVEN MOMENT — and nothing else.

   Pure, in the same sense core/anim.js and core/doc.js are: no DOM, no three,
   no S. It takes a video object's params and a scene time and returns a time
   in the source clip. That makes the one piece of real arithmetic in the
   video feature testable on its own, and keeps this module a leaf in the
   import graph.

   THE WHOLE POINT of this file being separate is that it has exactly one
   call site — state/timeline.js's applyAnimation. Preview, scrubbing, the
   still export and every motion-blur sub-frame of the video export all reach
   the decoder through that one function, so there is no second place where
   "which frame belongs at time t" could be answered differently. Two answers
   to that question is the failure mode this design exists to prevent: the
   preview is supposed to be a preview OF THE EXPORT, and a drift between
   them is invisible until someone compares the deliverable to what they
   approved on screen.

   The four params, and what each is for:

       offset   scene seconds at which the clip's in-point lands
       start    source seconds — the in-point, i.e. how much head is trimmed
       speed    source seconds per scene second; > 0 only
       loop     whether it repeats once it runs out

   Reverse (negative speed) is deliberately not supported. Every access path
   into the decoder — the export's timestamp array, the playback stream, the
   scrub — is cheapest when time only moves forward, and mediabunny's own
   optimised path explicitly checks for a backwards jump and flushes the
   decoder when it sees one. Reverse would turn the common case into the
   expensive one for a feature nobody asked for. */

const MIN_SPEED = 0.01;

/** Source seconds available after the trimmed head. */
const usableOf = P => Math.max(0, (+P.srcDur || 0) - Math.max(0, +P.start || 0));

/**
 * Scene time → source time, in seconds. Never null.
 *
 * Outside the clip's own span this HOLDS the nearest frame — the first one
 * before it starts, the last one after it ends — rather than going blank. A
 * signage plane that turns black because the clip ran out is a bug report,
 * not a feature, and "not on screen yet" is already expressible, precisely
 * and visibly, as a `visible` keyframe in the timeline.
 *
 * The returned value is deliberately allowed to equal the source duration at
 * the tail. The decoder's retrieval rule is "the last frame in presentation
 * order whose start timestamp is <= the one asked for", so a time at or past
 * the end resolves to the final frame rather than to nothing — verified
 * against mediabunny directly rather than assumed.
 *
 * @param {object} P  a video object's params
 * @param {number} t  scene seconds
 * @returns {number}  source seconds
 */
export function sourceTime(P, t) {
  const start = Math.max(0, +P.start || 0);
  const usable = usableOf(P);
  /* No duration yet means the clip's metadata has not been read — which
     happens for exactly one frame after a project loads, before the probe
     resolves. The in-point is the honest answer, and it is also the one that
     does not make the plane flash. */
  if (usable <= 0) return start;

  const speed = Math.max(MIN_SPEED, +P.speed || 1);
  const local = ((+t || 0) - (+P.offset || 0)) * speed;

  if (local <= 0) return start;                    // before it starts — hold frame one
  if (local < usable) return start + local;        // inside the clip
  /* Past the end. `local` is positive here, so % needs no sign correction —
     which it would if reverse playback were allowed. */
  return P.loop ? start + (local % usable) : start + usable;
}

/**
 * The scene-time window this clip actually covers, for anything that wants to
 * draw it — a clip strip in the timeline dock, or a warning that a clip is
 * shorter than the piece it sits in.
 *
 * `end` is where the source runs out, whether or not it loops; a looping clip
 * has no end in scene time, and the honest thing to report is the length of
 * one pass rather than the timeline's duration, which this module has no
 * business knowing about.
 */
export function clipSpan(P) {
  const offset = +P.offset || 0;
  const speed = Math.max(MIN_SPEED, +P.speed || 1);
  return { start: offset, end: offset + usableOf(P) / speed };
}
